import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Table } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import useHeaderGenerator from 'components/app/common/functions/useHeaderGenerator';
import FormModal from 'components/app/common/FormModal';
import LeadService from './LeadService';
import schema from './jsonSchema';
import DOMPurify from 'dompurify';
import LeadContactHistoryService from './LeadContactHistoryService';
import LeadContactHistoryModal from './LeadContactHistoryModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const LeadDetails = () => {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const [modalShow, setModalShow] = useState(false);
  const [event, setEvent] = useState();
  const [contactHistory, setContactHistory] = useState([]);
  const [showContactHistoryModal, setShowContactHistoryModal] = useState(false);
  const [selectedContactHistory, setSelectedContactHistory] = useState(null);

  const showValue = attributeName => {
    if (attributeName === 'comments') {
      const sanitizedHtml = DOMPurify.sanitize(event[attributeName]);
      return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
    }
    return event[attributeName];
  };

  useEffect(() => {
    LeadService.retrieveLead(identifier)
      .then(data => {
        setEvent(data);
      })
      .catch(error => {
        console.error('Error retrieving Lead', error);
      });

    // Fetch contact history
    fetchContactHistory();
  }, [identifier]);

  const fetchContactHistory = () => {
    LeadContactHistoryService.retrieveAllContactHistoryByLead(identifier)
      .then(data => {
        setContactHistory(data);
      })
      .catch(error => {
        console.error('Error retrieving Contact History', error);
      });
  };

  const handleAddContactHistory = () => {
    setSelectedContactHistory(null);
    setShowContactHistoryModal(true);
  };

  const handleEditContactHistory = history => {
    setSelectedContactHistory(history);
    setShowContactHistoryModal(true);
  };

  const handleDeleteContactHistory = historyId => {
    if (window.confirm('Are you sure you want to delete this contact history?')) {
      LeadContactHistoryService.deleteContactHistory(historyId)
        .then(() => {
          fetchContactHistory();
        })
        .catch(error => {
          console.error('Error deleting contact history', error);
        });
    }
  };

  const handleContactHistorySaved = () => {
    fetchContactHistory();
  };

  const buttonsConf = [
    {
      name: 'Edit Lead',
      type: 'button',
      icon: 'edit',
      variant: 'info',
      onClick: () => {
        console.log('Edit this event');
        navigate('/app/leads/' + identifier + '/edit');
      }
    },
    {
      name: 'Delete',
      type: 'button',
      icon: 'trash',
      variant: 'warning',
      onClick: () => {
        console.log('Delete Lead');
        LeadService.deleteLead(identifier)
          .then(data => {
            console.log(data);
            navigate('/app/leads');
          })
          .catch(error => console.log(error));
      }
    }
  ];

  const jsonSchema = schema;
  const headerGenerated = useHeaderGenerator('Lead', ' ', buttonsConf);

  return (
    <>
      {headerGenerated}
      {modalShow && (
        <FormModal
          message={'Lead has been updated!'}
          modalShow={modalShow}
          setModalShow={setModalShow}
        />
      )}
      {event && (
        <>
          <Card>
            <Card.Body>
              {jsonSchema.map(field => {
                if (field.type === 'group') {
                  return field.fields.map(f => (
                    <Row key={f.name}>
                      <Col xs={5} sm={4}>
                        <p className="fw-semi-bold mb-1">{f.label + ': '}</p>
                      </Col>
                      <Col>{showValue(f.name)}</Col>
                    </Row>
                  ));
                } else {
                  return (
                    <Row key={field.name}>
                      <Col xs={5} sm={4}>
                        <p className="fw-semi-bold mb-1">
                          {field.label + ': '}
                        </p>
                      </Col>
                      <Col>{showValue(field.name)}</Col>
                    </Row>
                  );
                }
              })}
            </Card.Body>
          </Card>

          {/* Contact History Section */}
          <Card className="mt-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Contact History</h5>
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddContactHistory}
              >
                <FontAwesomeIcon icon="plus" className="me-1" />
                Add Attempt
              </Button>
            </Card.Header>
            <Card.Body>
              {contactHistory.length === 0 ? (
                <p className="text-muted text-center py-3">
                  No contact history found. Click "Add Attempt" to create one.
                </p>
              ) : (
                <Table responsive striped bordered hover>
                  <thead>
                    <tr>
                      <th>Contact Date</th>
                      <th>Contact Type</th>
                      <th>Attempt Number</th>
                      <th>Profile</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contactHistory.map(history => (
                      <tr key={history.identifier}>
                        <td>{history.contactDate}</td>
                        <td>{history.contactType}</td>
                        <td>{history.attemptNumber}</td>
                        <td>
                          {history.profile
                            ? `${history.profile.firstName} ${history.profile.lastName}`
                            : 'N/A'}
                        </td>
                        <td>
                          <Button
                            variant="info"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEditContactHistory(history)}
                          >
                            <FontAwesomeIcon icon="edit" />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() =>
                              handleDeleteContactHistory(history.identifier)
                            }
                          >
                            <FontAwesomeIcon icon="trash" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {/* Contact History Modal */}
      <LeadContactHistoryModal
        show={showContactHistoryModal}
        onHide={() => setShowContactHistoryModal(false)}
        leadIdentifier={identifier}
        contactHistory={selectedContactHistory}
        onSaved={handleContactHistorySaved}
      />
    </>
  );
};

export default LeadDetails;
